<ng-container *ngIf="simulationDetails$ | async as simulationDetails">
  <ng-container *appVar="appLayout.isVertical$ | async as verticalLayout">
    <ng-container *ngIf="isQueued">
      <app-page>
        <div class="queued">
          <app-loading></app-loading>
          <p>Setting up simulation. This might take a minute...</p>
        </div>
      </app-page>
    </ng-container>

    <ng-container *ngIf="!isQueued">
      <ng-container
        *appVar="stateService.isUserLogged$ | async as isUserLogged"
      >
        <div
          style="
            width: 100%;
            max-width: 1400px;
            margin: auto;
            padding-left: 3em;
          "
        >
          <!-- Breadcrumb navigation -->
          <app-breadcrumb-nav
            *ngIf="isUserLogged && !isOpen"
            class="breadcrumb-nav"
            [breadcrumbs]="breadcrumbs"
            [style]="'COMPACT'"
          >
          </app-breadcrumb-nav>

          <button
            *ngIf="isOpen && history.length > 1"
            mat-button
            color="accent"
            (click)="goBack()"
            style="
              display: flex;
              justify-content: space-between;
              width: fit-content;
              margin: 2em 0;
            "
          >
            <mat-icon style="margin-right: 0.5em">arrow_back</mat-icon>Back
          </button>
        </div>

        <!-- Loading state -->
        <app-loading
          class="loading-state-wrapper"
          *ngIf="simulationDetails.isLoading"
        ></app-loading>

        <!-- Error state -->
        <app-error
          class="error-state-wrapper"
          *ngIf="simulationDetails.error as error"
          [error]="simulationDetails.error"
        ></app-error>

        <!-- Empty state -->
        <app-empty
          *ngIf="simulationDetails.didSucceed && !simulationDetails.value.name"
        ></app-empty>

        <ng-container *ngIf="simulationDetails.value">
          <app-page [ngClass]="{ 'sim-report-small-screen': verticalLayout }">
            <!-- Simulation: Header & Actions -->
            <app-header [verticalLayout]="verticalLayout">
              <h1
                app-overline
                style="
                  display: grid;
                  grid-template-columns: 1fr auto;
                  gap: 1em;
                  align-items: center;
                  margin-left: 1rem;
                "
                [ngStyle]="verticalLayout ? { 'line-height': '16px;' } : {}"
              >
                Simulation details
                <p
                  class="updatedMessageBanner"
                  *ngIf="simulationDetails.value.updatedMessage"
                >
                  <mat-icon>warning</mat-icon>
                  {{ simulationDetails.value.updatedMessage }}
                </p>
              </h1>

              <h2
                app-title
                style="
                  display: flex;
                  margin-bottom: 0;
                  align-items: center;
                  width: 100%;
                  margin-left: 1rem;
                "
              >
                {{ simulationDetails.value.name }}
                <button
                  *appIfCommandAvailable="'update_simulation'"
                  (click)="renameClick(simulationDetails.value.id)"
                  mat-icon-button
                  [color]="'primary'"
                  [disabled]="isThirdPartySimulation || simReportDisabled"
                >
                  <mat-icon>edit</mat-icon>
                </button>
                <span
                  *ngIf="simulationDetails.value.status[0]?.data.eta > 0"
                  style="font-size: 17px; color: gray; font-weight: 400"
                  >ETA:
                  {{
                    simulationDetails.value.status[0].data.eta.toFixed(0)
                  }}min</span
                >
              </h2>

              <app-company-logo
                *ngIf="!verticalLayout"
                [orgId]="simulationDetails.value.organization?.id"
                [isOpen]="isOpen"
                [assetUrl]="
                  simulationDetails.value.organization?.logo ||
                  publicCustomerOrganizationAsset
                "
              ></app-company-logo>

              <app-header-actions>
                <div
                  class="header-actions-wrapper"
                  [ngClass]="{ 'vertical-layout': verticalLayout }"
                >
                  <div class="action-buttons">
                    <ng-container
                      *ngIf="
                        isOpen &&
                        !simulationDetails.value.metadata?.quoteRequestedAt
                      "
                    >
                      <button
                        id="simulationButtonRequestQuote"
                        (click)="
                          openRequestQuoteDialog(simulationDetails.value.id)
                        "
                        mat-raised-button
                        pally-rounded-button-m
                        color="accent"
                      >
                        <mat-icon>email</mat-icon>
                        Get quote
                      </button>
                    </ng-container>

                    <!-- Downloads menu -->
                    <button
                      mat-raised-button
                      pally-rounded-button-m
                      color="primary"
                      matTooltip="All downloads"
                      [matMenuTriggerFor]="downloadsMenu"
                      (click)="isDownloadsMenuOpen = true"
                    >
                      <mat-icon
                        class="downloadsIconTransition"
                        [class.rotated90deg]="isDownloadsMenuOpen"
                        >chevron_right</mat-icon
                      >
                      Downloads
                    </button>
                    <mat-menu
                      #downloadsMenu="matMenu"
                      (close)="isDownloadsMenuOpen = false"
                    >
                      <!-- Pallet report download -->
                      <button
                        mat-menu-item
                        id="simulationButtonDownloadPalletReport"
                        [disabled]="!simulationDetails.value?.pattern?.data"
                        [matTooltip]="
                          simulationDetails.value?.pattern?.data
                            ? ''
                            : 'No pattern file available'
                        "
                        (click)="
                          downloadPalletReport(
                            simulationDetails.value.pattern.data
                          )
                        "
                      >
                        <mat-icon>download</mat-icon>Pallet report
                      </button>

                      <!-- Simulation report download -->
                      <button
                        mat-menu-item
                        id="simulationButtonDownloadSimulationReport"
                        [disabled]="simReportDisabled"
                        [ngStyle]="verticalLayout ? { width: '100%' } : {}"
                        (click)="
                          downloadSimulationReport(
                            simulationDetails.value.pattern?.data?.name
                          )
                        "
                      >
                        <mat-icon>download</mat-icon>Simulation report
                      </button>

                      <mat-divider></mat-divider>

                      <!-- Robot stl model download -->
                      <button
                        mat-menu-item
                        id="simulationButtonDownloadStlFile"
                        *ngIf="simviz?.simulationView"
                        [disabled]="
                          simviz?.simulationView?.loading$
                            ? (simviz?.simulationView?.loading$ | async).value
                            : true
                        "
                        (click)="downloadAsSTL(simulationDetails?.value?.name)"
                      >
                        <mat-icon>download</mat-icon>Robot as STL
                      </button>

                      <mat-divider></mat-divider>

                      <!-- URP + Installations download -->
                      <button
                        mat-menu-item
                        id="simulationButtonDownloadUrpFile"
                        *ngIf="hasURPAndInstallationUrls(artifactsURL)"
                        (click)="
                          downloadURPAndInstallation(
                            simulationDetails?.value?.name,
                            artifactsURL
                          )
                        "
                      >
                        <mat-icon>download</mat-icon>Installations & URPs
                      </button>
                    </mat-menu>

                    <button
                      mat-raised-button
                      pally-rounded-button-m
                      id="simulationButtonCopyLink"
                      color="primary"
                      (click)="copyShareLink()"
                    >
                      <mat-icon>share</mat-icon>COPY LINK
                    </button>
                  </div>
                  <div class="unit-system-toggle">
                    <p>Use Imperial System</p>
                    <mat-slide-toggle
                      color="primary"
                      [checked]="useImperial"
                      (change)="toggleUnitSystem()"
                    >
                    </mat-slide-toggle>
                  </div>
                </div>
              </app-header-actions>
            </app-header>
            <mat-divider
              *ngIf="simulationDetails.value.description"
            ></mat-divider>
            <p *ngIf="simulationDetails.value.description">
              {{ simulationDetails.value.description }}
            </p>

            <!-- Simulation: Player -->
            <div
              class="player-mode-swapping-container"
              *appVar="
                simulationStatuses.concluded.includes(
                  simulationDetails.value.simulation_state
                ) as isReadyToPlay
              "
              [ngClass]="{
                'theater-mode': simulationPlayerMode === playerMode.THEATER,
                'fullscreen-mode':
                  simulationPlayerMode === playerMode.FULLSCREEN
              }"
            >
              <div class="player-status-wrapper" *ngIf="isReadyToPlay">
                <div
                  class="player-wrapper"
                  [ngClass]="{ 'vertical-layout': verticalLayout }"
                >
                  <app-simviz
                    class="player"
                    [public]="!isUserLogged || isOpen"
                    [loadinfo]="simviz_loadinfo"
                    [labelOrientation]="project.data.box.label.orientation"
                    [movementBoxSet]="movementBoxes"
                    [playerMode]="simulationPlayerMode"
                    [productModelId]="
                      simulationDetails?.value?.pattern.data.guiSettings
                        ?.productModelId || 'box'
                    "
                    [isOpen]="isOpen"
                    (playerModeEvent)="togglePlayerMode($event)"
                  ></app-simviz>
                </div>

                <!-- Status Cards -->
                <div class="status-wrapper">
                  <ng-container *ngTemplateOutlet="SimulationStatusCards">
                  </ng-container>
                </div>
              </div>

              <div
                class="status-wrapper-row"
                [ngClass]="{
                  singleCol: verticalLayout,
                  threeCols: !verticalLayout,
                  'simulation-running': !isReadyToPlay
                }"
              >
                <ng-container *ngTemplateOutlet="SimulationStatusCards">
                </ng-container>
              </div>
            </div>
            <ng-template #SimulationStatusCards let-isReportPdf="isReportPdf">
              <mat-card
                appearance="outlined"
                [ngClass]="
                  getBgStatusClass(simulationDetails.value.simulation_state)
                "
                class="{{ verticalLayout ? 'card-bottom-margin' : '' }}"
              >
                <mat-card-subtitle>
                  STATUS
                  <button
                    *ngIf="!verticalLayout"
                    (click)="
                      openInfoPopup(
                        'status_' +
                          simulationDetails.value.simulation_state.toLowerCase(),
                        {
                          simulationDetails: simulationDetails.value
                        }
                      )
                    "
                  >
                    <mat-icon class="pdf-hide" svgIcon="info"></mat-icon>
                  </button>
                </mat-card-subtitle>
                <mat-card-title>
                  <ng-container
                    [ngSwitch]="simulationDetails.value.simulation_state"
                  >
                    <ng-container *ngSwitchCase="SimulationStatus.QUEUED">
                      <app-awaiting></app-awaiting>
                    </ng-container>
                    <ng-container *ngSwitchCase="SimulationStatus.INITIALIZING">
                      <app-awaiting></app-awaiting>
                    </ng-container>
                    <ng-container *ngSwitchCase="SimulationStatus.RUNNING">
                      <ng-container
                        *ngTemplateOutlet="showProgressCard"
                      ></ng-container>
                    </ng-container>
                    <ng-container
                      *ngSwitchCase="SimulationStatus.STARTING_ROBOT"
                    >
                      <app-awaiting></app-awaiting>
                    </ng-container>
                    <ng-container *ngSwitchCase="SimulationStatus.SETTING_UP">
                      <app-awaiting></app-awaiting>
                    </ng-container>
                    <ng-container *ngSwitchCase="SimulationStatus.PALLETIZING">
                      <ng-container
                        *ngTemplateOutlet="showProgressCard"
                      ></ng-container>
                    </ng-container>
                    <ng-template #showProgressCard>
                      <div
                        class="progress-spinner"
                        *ngIf="
                          simulationDetails.value.simulation_status &&
                          simulationDetails.value.simulation_status.progress
                        "
                      >
                        <mat-progress-spinner
                          #spinner
                          [mode]="'determinate'"
                          [diameter]="35"
                          [value]="
                            simulationDetails.value.simulation_status.progress
                          "
                        >
                        </mat-progress-spinner>
                        <span class="progress-spinner-text"
                          >{{
                            simulationDetails.value.simulation_status.progress
                          }}%</span
                        >
                      </div>
                    </ng-template>

                    <div class="pdf-hide">
                      <ng-container *ngSwitchCase="SimulationStatus.FINISHED">
                        <mat-icon>check_circle_outline</mat-icon>
                      </ng-container>
                      <ng-container *ngSwitchCase="SimulationStatus.SUCCESSFUL">
                        <mat-icon>check_circle_outline</mat-icon>
                      </ng-container>
                      <ng-container *ngSwitchCase="SimulationStatus.ERROR">
                        <mat-icon>error_outline</mat-icon>
                      </ng-container>
                      <ng-container *ngSwitchCase="SimulationStatus.CANCELED">
                        <mat-icon>highlight_off</mat-icon>
                      </ng-container>
                    </div>
                  </ng-container>
                  <span [ngClass]="{ 'status-card-title': isReportPdf }">
                    {{
                      simulationDetails.value.simulation_state.replaceAll(
                        '_',
                        ' '
                      )
                    }}
                  </span>
                </mat-card-title>
                <mat-card-subtitle
                  *ngIf="
                    (simulationDetails.value.simulation_state ===
                      SimulationStatus.ERROR ||
                      simulationDetails.value.simulation_state ===
                        SimulationStatus.FINISHED) &&
                    simulationDetails.value.error_details
                  "
                >
                  <div class="error-card">
                    {{ simulationDetails.value.error_details }}
                  </div>
                </mat-card-subtitle>
              </mat-card>
              <app-card-target
                *appVar="
                  simulationDetails.value?.pattern.data
                    .cpm as requiredPatternCPM
                "
                [value]="simulationDetails.value.status[0].data['CPM Achieved']"
                [target]="
                  requiredPatternCPM
                    ? requiredPatternCPM
                    : simulationDetails.value.status[0].data['CPM Required']
                "
                [ngClass]="{ 'card-bottom-margin': verticalLayout }"
                [icon]="isReportPdf ? null : 'conveyor_belt'"
                [failedBackground]="'default-sim'"
              >
                <app-card-subtitle>
                  CASES PER MINUTE &emsp;
                  <button
                    *ngIf="!verticalLayout"
                    (click)="
                      openInfoPopup('cpm', {
                        simulationDetails: simulationDetails.value
                      })
                    "
                  >
                    <mat-icon class="pdf-hide" svgIcon="info"></mat-icon>
                  </button>
                </app-card-subtitle>
              </app-card-target>

              <app-card-target
                [value]="
                  getAchivedMaxSpeed(
                    castToNumber(
                      simulationDetails.value.safety[2].data['Max Speed']
                    )
                  )
                "
                [target]="targetMaxSpeedValue(simulationDetails.value).value"
                [isInverted]="true"
                [reverseTargetTextDirection]="true"
                [targetText]="'Safety setting limit: '"
                [ngClass]="{ 'card-bottom-margin': verticalLayout }"
                [icon]="isReportPdf ? null : 'speed'"
              >
                <app-card-subtitle>
                  MAX SPEED ACHIEVED [{{
                    targetMaxSpeedValue(simulationDetails.value).unit
                  }}] <br />
                  Safety setting:
                  {{
                    (simulationDetails.value.safety[0]?.data?.Level
                      | split: '(?=[A-Z])':true) || 'N/A'
                  }}
                  <button
                    *ngIf="!verticalLayout"
                    (click)="
                      openInfoPopup('max_speed', {
                        safety_regulations: targetMaxSpeedValue(
                          simulationDetails.value
                        ).value
                      })
                    "
                  >
                    <mat-icon class="pdf-hide" svgIcon="info"></mat-icon>
                  </button>
                </app-card-subtitle>
              </app-card-target>
            </ng-template>

            <!-- Simulation: Share card -->
            <app-public-share-card
              class="public-share-card-wrapper"
              [isPublic]="(isPublic$ | async) || isThirdPartySimulation"
              [isUserLogged]="isUserLogged && !isOpen"
              [shareExpiration]="simSharedValidTo"
              [isDisabled]="
                isThirdPartySimulation ||
                !simulationStatuses.concluded.includes(
                  simulationDetails.value.simulation_state
                )
              "
              matTooltip="Wait until the simulation is finished"
              [matTooltipDisabled]="
                simulationStatuses.concluded.includes(
                  simulationDetails.value.simulation_state
                )
              "
              contentType="simulation"
              (setPublic)="setSimulationPublic($event.checked)"
              (updateExpiration)="
                updatePublicSimulationExpiration($event.share_expiry)
              "
              [verticalLayout]="verticalLayout"
            ></app-public-share-card>

            <ng-template
              #SummaryTabCards
              let-verticalLayout="verticalLayout"
              let-isReportPdf="isReportPdf"
            >
              <div class="simulation-details-summary-wrapper">
                <div
                  [ngClass]="{
                    'simulation-details-summary-grid': !verticalLayout
                  }"
                >
                  <div [ngClass]="verticalLayout ? 'singleCol' : 'twoCols'">
                    <mat-card
                      appearance="outlined"
                      [ngClass]="{ 'card-bottom-margin': verticalLayout }"
                    >
                      <mat-card-subtitle>
                        AVERAGE MINUTES PER PALLET
                        <button
                          *ngIf="!verticalLayout"
                          (click)="
                            openInfoPopup('avg_minutes_per_pallet', {
                              avg_minutes_per_pallet:
                                simulationDetails?.value?.status[0]?.data[
                                  'Avg time per pallet'
                                ] || 0
                            })
                          "
                        >
                          <mat-icon class="pdf-hide" svgIcon="info"></mat-icon>
                        </button>
                      </mat-card-subtitle>
                      <mat-card-title
                        style="display: flex; align-items: center; gap: 8px"
                      >
                        <mat-icon class="pdf-hide">av_timer</mat-icon>
                        {{
                          simulationDetails?.value?.status[0]?.data[
                            'Avg time per pallet'
                          ] || 0
                        }}
                      </mat-card-title>
                    </mat-card>
                    <mat-card
                      appearance="outlined"
                      [ngClass]="{ 'card-bottom-margin': verticalLayout }"
                    >
                      <mat-card-subtitle>
                        MAX. PRODUCTS PER PICK
                        <button
                          *ngIf="!verticalLayout"
                          (click)="openInfoPopup('max_pick')"
                        >
                          <mat-icon class="pdf-hide" svgIcon="info"></mat-icon>
                        </button>
                      </mat-card-subtitle>
                      <mat-card-title
                        style="display: flex; align-items: center; gap: 8px"
                      >
                        <mat-icon class="pdf-hide"
                          >precision_manufacturing</mat-icon
                        >{{
                          simulationDetails.value.simulation_status?.report_data
                            ?.max_picked_boxes ||
                            simulationDetails.value.pattern.data.maxGrip
                        }}</mat-card-title
                      >
                    </mat-card>
                  </div>

                  <mat-card
                    appearance="outlined"
                    [ngClass]="{ 'card-bottom-margin': verticalLayout }"
                  >
                    <div [ngClass]="verticalLayout ? 'singleCol' : 'fiveCols'">
                      <ng-container
                        *ngIf="
                          getProductDimensions(
                            simulationDetails.value
                          ) as prodDim
                        "
                      >
                        <div
                          class="infoCard"
                          [ngClass]="{ 'card-bottom-margin': verticalLayout }"
                        >
                          <mat-card-subtitle>
                            <div class="subtitle">
                              <mat-icon svgIcon="product"></mat-icon>
                              PRODUCT
                            </div>
                            <button
                              *ngIf="!verticalLayout"
                              (click)="
                                openInfoPopup('product', {
                                  prodDim: prodDim
                                })
                              "
                            >
                              <mat-icon
                                class="pdf-hide"
                                svgIcon="info"
                              ></mat-icon>
                            </button>
                          </mat-card-subtitle>
                          <mat-card-content style="padding: 0">
                            <div
                              *ngFor="
                                let data of prodDim | keyvalue: orderOriginal
                              "
                              class="keyvalueLabel"
                            >
                              <p>{{ data.key | titlecase }}</p>
                              <p *ngIf="data.key !== 'name'">
                                <span>{{ data.value }}</span>
                              </p>
                              <p *ngIf="data.key === 'name'">
                                {{ data.value }}
                              </p>
                            </div>
                          </mat-card-content>
                        </div>
                      </ng-container>

                      <mat-divider [vertical]="!verticalLayout"></mat-divider>

                      <ng-container
                        *ngIf="
                          getPalletDimensions(
                            simulationDetails.value
                          ) as pallDim
                        "
                      >
                        <div
                          class="infoCard"
                          [ngClass]="{ 'card-bottom-margin': verticalLayout }"
                        >
                          <mat-card-subtitle>
                            <div class="subtitle">
                              <mat-icon svgIcon="pallet"></mat-icon>
                              PALLET
                            </div>
                            <button
                              *ngIf="!verticalLayout"
                              (click)="
                                openInfoPopup('pallet', {
                                  simulationDetails: simulationDetails.value,
                                  pallDim: pallDim
                                })
                              "
                            >
                              <mat-icon
                                class="pdf-hide"
                                svgIcon="info"
                              ></mat-icon>
                            </button>
                          </mat-card-subtitle>
                          <mat-card-content style="padding: 0">
                            <div
                              *ngFor="
                                let data of pallDim | keyvalue: orderOriginal
                              "
                              class="keyvalueLabel"
                            >
                              <p>{{ data.key | titlecase }}</p>
                              <p>
                                {{ data.value }}
                              </p>
                            </div>
                          </mat-card-content>
                        </div>
                      </ng-container>

                      <mat-divider [vertical]="!verticalLayout"></mat-divider>

                      <ng-container
                        *ngIf="
                          getPatternData(simulationDetails.value) as pattern
                        "
                      >
                        <div
                          class="infoCard"
                          [ngClass]="{ 'card-bottom-margin': verticalLayout }"
                        >
                          <mat-card-subtitle>
                            <div class="subtitle">
                              <mat-icon svgIcon="pattern"></mat-icon>
                              PATTERN
                            </div>
                            <button
                              *ngIf="!verticalLayout"
                              (click)="
                                openInfoPopup('pattern', {
                                  pattern: pattern
                                })
                              "
                            >
                              <mat-icon
                                class="pdf-hide"
                                svgIcon="info"
                              ></mat-icon>
                            </button>
                          </mat-card-subtitle>
                          <mat-card-content style="padding: 0">
                            <div
                              *ngFor="
                                let data of pattern | keyvalue: orderOriginal
                              "
                              class="keyvalueLabel"
                            >
                              <p>{{ data.key }}</p>
                              <p>{{ data.value }}</p>
                            </div>
                          </mat-card-content>
                        </div>
                      </ng-container>
                    </div>
                  </mat-card>

                  <!-- Secondary pattern info card -->
                  <ng-container *ngIf="simulationDetails.value.second_pattern">
                    <mat-card
                      appearance="outlined"
                      [ngClass]="{ 'card-bottom-margin': verticalLayout }"
                    >
                      <div
                        [ngClass]="verticalLayout ? 'singleCol' : 'fiveCols'"
                      >
                        <ng-container
                          *ngIf="
                            getProductDimensions(
                              simulationDetails.value,
                              true
                            ) as prodDim
                          "
                        >
                          <div class="infoCard">
                            <mat-card-subtitle>
                              <div class="subtitle">
                                <mat-icon svgIcon="product"></mat-icon>
                                PRODUCT
                              </div>
                              <button
                                *ngIf="!verticalLayout"
                                (click)="
                                  openInfoPopup('product', {
                                    prodDim: prodDim
                                  })
                                "
                              >
                                <mat-icon
                                  class="pdf-hide"
                                  svgIcon="info"
                                ></mat-icon>
                              </button>
                            </mat-card-subtitle>
                            <mat-card-content style="padding: 0">
                              <div
                                *ngFor="
                                  let data of prodDim | keyvalue: orderOriginal
                                "
                                class="keyvalueLabel"
                              >
                                <p>{{ data.key | titlecase }}</p>
                                <p *ngIf="data.key !== 'name'">
                                  <span>{{ data.value }}</span>
                                </p>
                                <p *ngIf="data.key === 'name'">
                                  {{ data.value }}
                                </p>
                              </div>
                            </mat-card-content>
                          </div>
                        </ng-container>

                        <mat-divider [vertical]="!verticalLayout"></mat-divider>

                        <ng-container
                          *ngIf="
                            getPalletDimensions(
                              simulationDetails.value,
                              true
                            ) as pallDim
                          "
                        >
                          <div class="infoCard">
                            <mat-card-subtitle>
                              <div class="subtitle">
                                <mat-icon svgIcon="pallet"></mat-icon>
                                PALLET
                              </div>
                              <button
                                *ngIf="!verticalLayout"
                                (click)="
                                  openInfoPopup('pallet', {
                                    simulationDetails: simulationDetails.value,
                                    pallDim: pallDim
                                  })
                                "
                              >
                                <mat-icon
                                  class="pdf-hide"
                                  svgIcon="info"
                                ></mat-icon>
                              </button>
                            </mat-card-subtitle>
                            <mat-card-content style="padding: 0">
                              <div
                                *ngFor="
                                  let data of pallDim | keyvalue: orderOriginal
                                "
                                class="keyvalueLabel"
                              >
                                <p>{{ data.key | titlecase }}</p>
                                <p>
                                  {{ data.value }}
                                </p>
                              </div>
                            </mat-card-content>
                          </div>
                        </ng-container>

                        <mat-divider [vertical]="!verticalLayout"></mat-divider>

                        <ng-container
                          *ngIf="
                            getPatternData(
                              simulationDetails.value,
                              true
                            ) as pattern
                          "
                        >
                          <div class="infoCard">
                            <mat-card-subtitle>
                              <div class="subtitle">
                                <mat-icon svgIcon="pattern"></mat-icon>
                                PATTERN
                              </div>
                              <button
                                *ngIf="!verticalLayout"
                                (click)="
                                  openInfoPopup('pattern', {
                                    pattern: pattern
                                  })
                                "
                              >
                                <mat-icon
                                  class="pdf-hide"
                                  svgIcon="info"
                                ></mat-icon>
                              </button>
                            </mat-card-subtitle>
                            <mat-card-content style="padding: 0">
                              <div
                                *ngFor="
                                  let data of pattern | keyvalue: orderOriginal
                                "
                                class="keyvalueLabel"
                              >
                                <p>{{ data.key }}</p>
                                <p>{{ data.value }}</p>
                              </div>
                            </mat-card-content>
                          </div>
                        </ng-container>
                      </div>
                    </mat-card>
                  </ng-container>
                </div>
              </div>
            </ng-template>

            <mat-tab-group
              (selectedTabChange)="tabGroupClick($event)"
              mat-stretch-tabs
              *appIfCommandAvailable="'view_simulation_values'"
            >
              <mat-tab label="Summary">
                <ng-container
                  [ngTemplateOutlet]="SummaryTabCards"
                  [ngTemplateOutletContext]="{ $implicit: verticalLayout }"
                ></ng-container>
              </mat-tab>

              <mat-tab label="Hardware">
                <app-table
                  [strictFirstColWidth]="true"
                  [showPaginator]="false"
                  [actions]="false"
                  [clickableRows]="false"
                  [topRow]="false"
                  [inputData]="tableData.hw"
                  [displayedColumns]="displayedColumns.hw"
                ></app-table>
              </mat-tab>

              <mat-tab
                label="{{
                  verticalLayout ? 'Installation' : 'Installation setup'
                }}"
              >
                <app-table
                  [strictFirstColWidth]="true"
                  [showPaginator]="false"
                  [actions]="false"
                  [clickableRows]="false"
                  [topRow]="false"
                  [inputData]="tableData.installation"
                  [displayedColumns]="displayedColumns.installation"
                ></app-table>
              </mat-tab>

              <mat-tab label="Software">
                <app-table
                  [strictFirstColWidth]="true"
                  [showPaginator]="false"
                  [actions]="false"
                  [clickableRows]="false"
                  [topRow]="false"
                  [inputData]="tableData.sw"
                  [displayedColumns]="displayedColumns.sw"
                ></app-table>

                <app-table
                  [strictFirstColWidth]="true"
                  [showPaginator]="false"
                  [actions]="false"
                  [clickableRows]="false"
                  [topRow]="false"
                  [inputData]="tableData.pally"
                  [displayedColumns]="displayedColumns.pally"
                ></app-table>
              </mat-tab>

              <mat-tab label="Power consumption">
                <app-table
                  [strictFirstColWidth]="true"
                  [showPaginator]="false"
                  [actions]="false"
                  [clickableRows]="false"
                  [topRow]="false"
                  [inputData]="tableData.power"
                  [displayedColumns]="displayedColumns.power"
                ></app-table>
              </mat-tab>

              <mat-tab
                *ngIf="!isOpen && tableData.collisions.length"
                label="Collisions"
              >
                <app-table
                  [strictFirstColWidth]="true"
                  [showPaginator]="false"
                  [actions]="false"
                  [clickableRows]="false"
                  [topRow]="false"
                  [inputData]="tableData.collisions"
                  [displayedColumns]="displayedColumns.collisions"
                ></app-table>
              </mat-tab>

              <mat-tab
                *ngIf="!isOpen && tableData.movementData?.length"
                label="Safety"
              >
                <div>
                  <app-table
                    *ngIf="tableData.movementBoxes?.length"
                    [strictFirstColWidth]="false"
                    [showPaginator]="false"
                    [actions]="false"
                    [clickableRows]="false"
                    [topRow]="false"
                    [inputData]="tableData.movementBoxes"
                    [displayedColumns]="displayedColumns.movementBoxes"
                  ></app-table>
                  <app-table
                    [strictFirstColWidth]="false"
                    [showPaginator]="false"
                    [actions]="false"
                    [clickableRows]="false"
                    [topRow]="false"
                    [inputData]="tableData.movementData"
                    [displayedColumns]="displayedColumns.movementData"
                  ></app-table>
                  <app-table
                    [strictFirstColWidth]="true"
                    [showPaginator]="false"
                    [actions]="false"
                    [clickableRows]="false"
                    [topRow]="false"
                    [inputData]="tableData.safety_settings"
                    [displayedColumns]="displayedColumns.safety_settings"
                  ></app-table>
                  <p class="helpText">
                    The table shows maximum speed achieved and number of moves
                    for the different relevant payloads during simulation. The
                    first row indicates data when there are no boxes attached,
                    this is when the robot is returning to pickup position. The
                    following rows show maximum speed achieved and number of
                    moves for single pick, double pick, and so on.
                  </p>
                </div>
              </mat-tab>
            </mat-tab-group>

            <div class="simulation-report-layout" #simulationReport>
              <app-header class="report-header">
                <h1
                  app-overline
                  [ngStyle]="verticalLayout ? { 'line-height': '16px;' } : {}"
                >
                  Simulation report
                </h1>

                <h2 app-title>
                  {{ simulationDetails.value.name }}
                </h2>

                <app-company-logo
                  [orgId]="simulationDetails.value.organization?.id"
                  [isOpen]="isOpen"
                  [assetUrl]="
                    simulationDetails.value.organization?.logo ||
                    publicCustomerOrganizationAsset
                  "
                ></app-company-logo>

                <app-header-actions class="header-actions">
                  <img [src]="publicApi.mrcLogoBlue" />
                </app-header-actions>
              </app-header>

              <h2>Summary</h2>
              <div class="threeCols">
                <ng-container
                  [ngTemplateOutlet]="SimulationStatusCards"
                  [ngTemplateOutletContext]="{
                    isReportPdf: true
                  }"
                ></ng-container>
              </div>

              <ng-container
                [ngTemplateOutlet]="SummaryTabCards"
                [ngTemplateOutletContext]="{ verticalLayout: false }"
              ></ng-container>

              <h2>Hardware</h2>
              <div class="twoCols">
                <app-table
                  [strictFirstColWidth]="true"
                  [showPaginator]="false"
                  [actions]="false"
                  [clickableRows]="false"
                  [topRow]="false"
                  [inputData]="tableData.hw"
                  [displayedColumns]="displayedColumns.hw"
                ></app-table>
                <app-table
                  [strictFirstColWidth]="true"
                  [showPaginator]="false"
                  [actions]="false"
                  [clickableRows]="false"
                  [topRow]="false"
                  [inputData]="tableData.installation"
                  [displayedColumns]="displayedColumns.installation"
                ></app-table>
              </div>

              <h2>Software</h2>
              <div class="twoCols">
                <app-table
                  [strictFirstColWidth]="true"
                  [showPaginator]="false"
                  [actions]="false"
                  [clickableRows]="false"
                  [topRow]="false"
                  [inputData]="tableData.pally"
                  [displayedColumns]="displayedColumns.pally"
                ></app-table>
                <app-table
                  [strictFirstColWidth]="true"
                  [showPaginator]="false"
                  [actions]="false"
                  [clickableRows]="false"
                  [topRow]="false"
                  [inputData]="tableData.sw"
                  [displayedColumns]="displayedColumns.sw"
                ></app-table>
              </div>

              <h2 style="margin-top: 150px">Power consumption</h2>
              <div class="twoCols">
                <app-table
                  [strictFirstColWidth]="true"
                  [showPaginator]="false"
                  [actions]="false"
                  [clickableRows]="false"
                  [topRow]="false"
                  [inputData]="tableData.power"
                  [displayedColumns]="displayedColumns.power"
                ></app-table>
              </div>
              <div class="singleCol">
                <h2 style="margin-top: 150px">Safety</h2>
                <app-table
                  *ngIf="tableData.movementBoxes?.length"
                  [strictFirstColWidth]="false"
                  [showPaginator]="false"
                  [actions]="false"
                  [clickableRows]="false"
                  [topRow]="false"
                  [inputData]="tableData.movementBoxes"
                  [displayedColumns]="displayedColumns.movementBoxes"
                ></app-table>
                <app-table
                  [strictFirstColWidth]="false"
                  [showPaginator]="false"
                  [actions]="false"
                  [clickableRows]="false"
                  [topRow]="false"
                  [inputData]="tableData.movementData"
                  [displayedColumns]="displayedColumns.movementData"
                ></app-table>
                <app-table
                  [strictFirstColWidth]="false"
                  [showPaginator]="false"
                  [actions]="false"
                  [clickableRows]="false"
                  [topRow]="false"
                  [inputData]="tableData.safety_settings"
                  [displayedColumns]="displayedColumns.safety_settings"
                ></app-table>
              </div>
            </div>

            <ng-container
              *ngIf="contentfulSimFooterElements$ | async as footerElements"
            >
              <mat-divider
                *ngIf="footerElements.value?.length"
                [ngStyle]="
                  verticalLayout
                    ? { margin: '2em 0 3em' }
                    : { margin: '1em 0 2em' }
                "
              >
              </mat-divider>

              <!-- Loading state -->
              <app-loading *ngIf="footerElements.isLoading"></app-loading>

              <!-- Error state -->
              <app-error
                *ngIf="footerElements.error as error"
                [error]="error"
                [hideActions]="true"
              ></app-error>

              <ng-container
                *ngIf="footerElements.didSucceed && footerElements.value"
              >
                <div class="alternate-right-left">
                  <div *ngFor="let footerElement of footerElements.value">
                    <div class="image-container" *ngIf="footerElement.image">
                      <img [src]="footerElement.image?.url" />
                    </div>

                    <div class="info-container">
                      <h2>{{ footerElement.header }}</h2>

                      <p
                        [innerHtml]="
                          footerElement.description.json
                            | richtextDocumentToHtml
                        "
                      ></p>

                      <div
                        class="button-container"
                        *ngIf="footerElement?.actionButtons"
                      >
                        <ng-container
                          *ngFor="
                            let item of footerElement.actionButtons?.buttons
                          "
                        >
                          <button
                            mat-raised-button
                            pally-rounded-button-m
                            (click)="clickLink(item.url)"
                          >
                            {{ item.title }}
                          </button>
                        </ng-container>
                      </div>
                    </div>
                  </div>
                </div>
              </ng-container>
            </ng-container>

            <!-- Comment Section: -->
            <ng-container>
              <mat-divider style="margin: 2em 0 3em"></mat-divider>
              <app-comments
                [simulationId]="simulationDetails.value.id"
                [accessMode]="
                  isOpen
                    ? CommentAccessMode.OPEN
                    : isUserLogged
                    ? CommentAccessMode.PRIVATE
                    : CommentAccessMode.PUBLIC
                "
                [user]="stateService.user$ | async"
                [lastActionTimestamp]="lastActionTimestamp"
                [simulationOrgId]="simulationDetails.value.organization?.id"
                (actionPerformed)="updateLastActionPerformedTimestamp()"
              >
              </app-comments>
            </ng-container>

            <!-- Pallet Report PDF generation (Will expand out of sight for a moment) -->
            <app-pallet-report
              class="pdf-report"
              *ngIf="showReport"
              #palletReport
              [patternData]="patternReportData"
              [assetUrl]="
                simulationDetails.value.organization?.logo ||
                publicCustomerOrganizationAsset
              "
            >
            </app-pallet-report>
          </app-page>
        </ng-container>
      </ng-container>
    </ng-container>
  </ng-container>
</ng-container>
